import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, Loader2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './assetExport.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ClosureDialog = ({ items, busy, error, onClose, onRead }) => {
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return <dialog ref={ref} className="asset-export-dialog" style={{ width: '34rem' }}
    aria-labelledby="route-closure-title" data-testid="route-closure-popup"
    onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
    <div className="flex items-start justify-between gap-3 shrink-0">
      <h2 id="route-closure-title" className="text-base md:text-lg font-semibold flex gap-2 items-center" data-testid="route-closure-title"><AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />Cambios en tus recorridos</h2>
      <button onClick={onClose} disabled={busy} aria-label="Cerrar aviso" title="Cerrar aviso" className="p-1 rounded hover:bg-black/5" data-testid="route-closure-close"><X className="w-5 h-5" /></button>
    </div>
    <ul className="my-4 divide-y divide-slate-200 min-h-0 overflow-y-auto">
      {items.map(item => <li key={item.id} className="py-3 space-y-1" data-testid={`route-closure-item-${item.id}`}>
        <p className="text-sm font-semibold [overflow-wrap:anywhere]" data-testid={`route-closure-message-${item.id}`}>{item.mensaje}</p>
        <p className="text-xs text-slate-500 [overflow-wrap:anywhere]" data-testid={`route-closure-routes-${item.id}`}>{(item.recorrido_nombres || []).join(' · ')}</p>
        <p className="text-xs text-slate-500" data-testid={`route-closure-time-${item.id}`}>{new Date(item.created_at).toLocaleString('es-ES')}</p>
      </li>)}
    </ul>
    {error && <p role="alert" className="text-sm text-red-700 mb-3" data-testid="route-closure-error">{error}</p>}
    <div className="flex justify-end shrink-0">
      <button onClick={onRead} disabled={busy} className="btn-primary text-sm flex items-center gap-2" data-testid="route-closure-acknowledge">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}{busy ? 'Guardando…' : 'Entendido'}
      </button>
    </div>
  </dialog>;
};

export const RouteClosurePopup = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const shown = useRef(new Set());
  const loading = useRef(false);
  const generation = useRef(0);
  const load = useCallback(async () => {
    if (!user?.id || loading.current || document.visibilityState === 'hidden') return;
    loading.current = true;
    const current = generation.current;
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true,
        params: { tipo: 'recorrido_activo_cerrado', unread: true, limit: 200 } });
      if (current !== generation.current) return;
      const unread = data.items || [];
      const incoming = unread.filter(item => !shown.current.has(item.id));
      incoming.forEach(item => shown.current.add(item.id));
      const unreadIds = new Set(unread.map(item => item.id));
      setItems(previous => [...previous.filter(item => unreadIds.has(item.id)), ...incoming]);
      if (incoming.length) window.dispatchEvent(new CustomEvent('xenicos:recorridos-changed'));
    } catch { /* El aviso persiste en el servidor; se reintenta al actualizar la campanita. */ }
    finally { if (current === generation.current) loading.current = false; }
  }, [user?.id]);
  useEffect(() => {
    generation.current += 1;
    loading.current = false;
    shown.current = new Set();
    setItems([]);
    load();
    window.addEventListener('xenicos:notifications-refreshed', load);
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', load);
    return () => {
      generation.current += 1;
      window.removeEventListener('xenicos:notifications-refreshed', load);
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', load);
    };
  }, [load]);
  const acknowledge = async () => {
    if (busy) return;
    setBusy(true); setError('');
    const acknowledged = new Set(items.map(item => item.id));
    try {
      await Promise.all(items.map(item => axios.put(`${API}/notifications/${encodeURIComponent(item.id)}/read`, {}, { withCredentials: true })));
      setItems(previous => previous.filter(item => !acknowledged.has(item.id)));
      window.dispatchEvent(new CustomEvent('xenicos:refresh-bell'));
    } catch { setError('No se pudo confirmar el aviso. Inténtalo de nuevo.'); }
    finally { setBusy(false); }
  };
  return items.length ? <ClosureDialog items={items} busy={busy} error={error}
    onClose={() => { if (!busy) { setItems([]); setError(''); } }} onRead={acknowledge} /> : null;
};